/**
 * Which products let the client write a note of their own on the line.
 *
 * Legacy offered one box, on the editing step only, and skipped three kinds:
 * floor-plan certification (a form, not a brief), AI photo editing (its settings
 * ARE the instruction) and 2D floor-plan editing (same, via its own settings
 * panel). This reproduces that gate rather than widening it — the v2 wire has a
 * `comment` on every line, so widening is a product decision, not a port.
 *
 * Legacy keyed on its `ProductCategory` enum, which v2 has no equivalent of on
 * the catalogue; the category SLUG that maps onto `EditingCategory.EDITING` is
 * the same signal (see `helpers/deriveOrderCategory`). Note `virtual_staging`
 * maps onto a DIFFERENT legacy category, so staging correctly gets no box.
 */

import { ProductKind } from 'constants/product'
import { EDITING_CATEGORY_ID } from 'dataQueries/purchaseFlowRevampCatalogue.transform'
import type { ConfigurationFlowId } from 'models/purchaseFlowRevamp'

import { configurationOwnsUpload } from './uploadRequirement'

/** Catalogue category slug for the legacy `EditingCategory.EDITING`. */

const KINDS_WITHOUT_COMMENT: ReadonlySet<string> = new Set<string>([
  ProductKind.FLOOR_PLAN_CERTIFICATION,
  ProductKind.AI_PHOTO_EDITING,
  ProductKind.FLOOR_PLAN_EDITING_2D,
])

/**
 * @example
 * acceptsProductComment({ categoryId: shell?.categoryId, kind: selectedVariant?.kind ?? shell?.kind })
 */
export const acceptsProductComment = (
  product: { categoryId?: string, kind?: string }
): boolean =>
  product.categoryId === EDITING_CATEGORY_ID && !KINDS_WITHOUT_COMMENT.has(product.kind ?? '')

/** Where a product's comment box is offered, if anywhere. */
export type ProductCommentSurface = 'drawer' | 'configuration' | 'none'

/**
 * Which surface offers the box.
 *
 * A brief describes the visuals it is written about, so it belongs beside them.
 * Since ISSUE-3386 those visuals are handed over on the configuration page for
 * every product that has one — and the drawer kept the box, so the client
 * uploaded their photos on one screen and went back to another to say what to do
 * with them. The same `configurationOwnsUpload` rule decides both, which is what
 * keeps them from drifting apart again.
 *
 * Exactly ONE surface ever offers it. Two boxes bound to one `CartItem.note`
 * would be worse than either, so the drawer asks this rather than testing the
 * flows itself.
 */
export const productCommentSurface = (
  product: { categoryId?: string, kind?: string, configurationFlows?: ConfigurationFlowId[] }
): ProductCommentSurface => {
  if (!acceptsProductComment(product)) return 'none'
  return configurationOwnsUpload(product.configurationFlows) ? 'configuration' : 'drawer'
}
